import { useState, useEffect, useRef, useCallback, useImperativeHandle, forwardRef } from 'react';
import { GoogleMap, Marker, InfoWindow, Polyline, DirectionsRenderer } from '@react-google-maps/api';
import { StatusBadge } from '../jobs/StatusBadge';
import { useGoogleMaps } from '../../context/GoogleMapsContext';

const STATUS_COLORS = {
  pending:    '#94a3b8',
  assigned:   '#3b82f6',
  on_the_way: '#f97316',
  started:    '#f59e0b',
  completed:  '#22c55e',
  approved:   '#059669',
  cancelled:  '#ef4444',
};
const STATUS_SYMBOLS = {
  completed:  '✓',
  approved:   '✓✓',
  on_the_way: '→',
  started:    '▶',
  cancelled:  '✗',
};

function jobIcon(status, selected) {
  const color  = STATUS_COLORS[status] ?? '#94a3b8';
  const symbol = STATUS_SYMBOLS[status] ?? '';
  const size = selected ? 36 : 28;
  const pin  = selected ? 46 : 36;
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${pin}" viewBox="0 0 ${size} ${pin}">
    <path d="M${size/2} 0 C${size*0.225} 0 0 ${size*0.225} 0 ${size/2} c0 ${pin*0.39} ${size/2} ${pin*0.61} ${size/2} ${pin*0.61} s${size/2}-${pin*0.37} ${size/2}-${pin*0.61} C${size} ${size*0.225} ${size*0.775} 0 ${size/2} 0z"
      fill="${color}" stroke="white" stroke-width="${selected ? 3 : 2}"/>
    <text x="${size/2}" y="${size*0.54}" text-anchor="middle" dominant-baseline="middle"
      fill="white" font-size="${selected ? 14 : 11}" font-weight="bold" font-family="sans-serif">${symbol}</text>
  </svg>`;
  return {
    url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
    scaledSize: new window.google.maps.Size(size, pin),
    anchor: new window.google.maps.Point(size / 2, pin),
  };
}

/**
 * Personel konum taze mi? recorded_at'a göre renk döndür.
 * < 2 dk → yeşil (aktif), 2–10 dk → turuncu (uyarı), > 10 dk → kırmızı (bağlantı kesildi)
 */
const AVAILABILITY_COLORS = {
  break: '#f59e0b',       // sarı
  lunch: '#f97316',       // turuncu
  unavailable: '#9ca3af', // gri
};

function staffFreshnessColor(recordedAt, availabilityStatus) {
  // availability_status öncelikli
  if (availabilityStatus && AVAILABILITY_COLORS[availabilityStatus]) {
    return AVAILABILITY_COLORS[availabilityStatus];
  }
  if (!recordedAt) return '#ef4444'; // kırmızı — sinyal yok
  const ageMs = Date.now() - new Date(recordedAt).getTime();
  const ageMin = ageMs / 60000;
  if (ageMin <= 2) return '#22c55e';   // yeşil
  if (ageMin <= 10) return '#f59e0b';  // turuncu
  return '#ef4444';                     // kırmızı
}

function staffFreshnessLabel(recordedAt) {
  if (!recordedAt) return 'Sinyal yok';
  const ageMs = Date.now() - new Date(recordedAt).getTime();
  const ageMin = Math.floor(ageMs / 60000);
  if (ageMin < 1) return 'Az önce';
  if (ageMin < 60) return `${ageMin} dk önce`;
  const ageH = Math.floor(ageMin / 60);
  return `${ageH} saat önce`;
}

function staffIcon(name, userId, recordedAt, availabilityStatus) {
  const color   = staffFreshnessColor(recordedAt, availabilityStatus);
  const initial = name?.charAt(0)?.toUpperCase() ?? '?';
  const label   = (name?.split(' ')[0] ?? name ?? '').slice(0, 9);
  const w       = Math.max(40, label.length * 7 + 14);

  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="56" viewBox="0 0 ${w} 56">
    <circle cx="${w/2}" cy="18" r="16" fill="${color}" stroke="white" stroke-width="2.5"/>
    <text x="${w/2}" y="18" text-anchor="middle" dominant-baseline="middle"
      fill="white" font-size="13" font-weight="bold" font-family="sans-serif">${initial}</text>
    <rect x="1" y="36" width="${w-2}" height="18" rx="4" fill="${color}" opacity="0.92"/>
    <text x="${w/2}" y="45" text-anchor="middle" dominant-baseline="middle"
      fill="white" font-size="9" font-weight="bold" font-family="sans-serif">${label}</text>
  </svg>`;
  return {
    url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
    scaledSize: new window.google.maps.Size(w, 56),
    anchor: new window.google.maps.Point(w / 2, 18),
  };
}

const MAP_OPTIONS = {
  disableDefaultUI: false,
  mapTypeControl: false,
  streetViewControl: false,
  fullscreenControl: true,
  styles: [
    { featureType: 'poi', elementType: 'labels', stylers: [{ visibility: 'off' }] },
  ],
};

const ISTANBUL = { lat: 41.015, lng: 28.979 };

export const FieldMap = forwardRef(function FieldMap(
  { jobs = [], locations = {}, onJobClick, selectedJobId, locationTrails = {}, directionsRoutes = {} },
  ref
) {
  const { isLoaded } = useGoogleMaps();
  const mapRef = useRef(null);
  const [activeMarker, setActiveMarker] = useState(null);
  const [renderedDirections, setRenderedDirections] = useState({});

  // Directions API ile rota hesapla
  useEffect(() => {
    if (!isLoaded || !window.google) return;
    const routeKeys = Object.keys(directionsRoutes);
    if (routeKeys.length === 0) {
      setRenderedDirections({});
      return;
    }

    const service = new window.google.maps.DirectionsService();
    const newDirections = {};

    Promise.all(routeKeys.map(async (jobId) => {
      const r = directionsRoutes[jobId];
      try {
        const result = await service.route({
          origin: r.origin,
          destination: r.destination,
          travelMode: window.google.maps.TravelMode.DRIVING,
        });
        newDirections[jobId] = { result, staffName: r.staffName, jobTitle: r.jobTitle };
      } catch {}
    })).then(() => setRenderedDirections(newDirections));
  }, [isLoaded, directionsRoutes]);

  useImperativeHandle(ref, () => ({
    panTo(lat, lng, zoom = 16) {
      if (mapRef.current) {
        mapRef.current.panTo({ lat, lng });
        mapRef.current.setZoom(zoom);
      }
    },
    fitBounds() {
      if (!mapRef.current) return;
      const bounds = new window.google.maps.LatLngBounds();
      let c = 0;
      jobs.forEach(j => {
        const la = parseFloat(j.latitude), lo = parseFloat(j.longitude);
        if (isFinite(la) && isFinite(lo)) { bounds.extend({ lat: la, lng: lo }); c++; }
      });
      Object.values(locations).forEach(l => {
        const la = parseFloat(l.latitude), lo = parseFloat(l.longitude);
        if (isFinite(la) && isFinite(lo)) { bounds.extend({ lat: la, lng: lo }); c++; }
      });
      if (c) { mapRef.current.fitBounds(bounds, 40); if (c === 1) mapRef.current.setZoom(14); }
    },
  }), [jobs, locations]);

  const onMapLoad = useCallback((map) => {
    mapRef.current = map;
    const bounds = new window.google.maps.LatLngBounds();
    let count = 0;
    jobs.forEach(j => {
      const lat = parseFloat(j.latitude), lng = parseFloat(j.longitude);
      if (isFinite(lat) && isFinite(lng)) { bounds.extend({ lat, lng }); count++; }
    });
    Object.values(locations).forEach(loc => {
      const lat = parseFloat(loc.latitude), lng = parseFloat(loc.longitude);
      if (isFinite(lat) && isFinite(lng)) { bounds.extend({ lat, lng }); count++; }
    });
    if (count > 0) {
      map.fitBounds(bounds, { top: 40, right: 40, bottom: 40, left: 40 });
      if (count === 1) map.setZoom(14);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (!isLoaded) {
    return (
      <div className="h-full w-full bg-slate-100 flex items-center justify-center">
        <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const validJobs = jobs.filter(j => {
    const lat = parseFloat(j.latitude), lng = parseFloat(j.longitude);
    return isFinite(lat) && isFinite(lng);
  });

  return (
    <GoogleMap
      mapContainerClassName="h-full w-full"
      center={ISTANBUL}
      zoom={11}
      options={MAP_OPTIONS}
      onLoad={onMapLoad}
      onClick={() => setActiveMarker(null)}
    >
      {validJobs.map(job => (
        <Marker
          key={job.id}
          position={{ lat: parseFloat(job.latitude), lng: parseFloat(job.longitude) }}
          icon={jobIcon(job.status, selectedJobId === job.id)}
          onClick={() => {
            setActiveMarker({ type: 'job', id: job.id });
            onJobClick?.(job);
          }}
          zIndex={selectedJobId === job.id ? 10 : 1}
        >
          {activeMarker?.type === 'job' && activeMarker.id === job.id && (
            <InfoWindow onCloseClick={() => setActiveMarker(null)}>
              <div style={{ minWidth: 180 }}>
                <p style={{ fontWeight: 600, color: '#1e293b', marginBottom: 4 }}>{job.title}</p>
                <div style={{ marginBottom: 4 }}><StatusBadge status={job.status} /></div>
                {job.address && <p style={{ fontSize: 12, color: '#64748b' }}>{job.address}</p>}
                {job.assigned_to_name && (
                  <p style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>
                    {job.assigned_to_name}
                  </p>
                )}
              </div>
            </InfoWindow>
          )}
        </Marker>
      ))}

      {Object.values(locations).map(loc => {
        const ts = loc.recorded_at || loc.timestamp;
        const freshnessText = staffFreshnessLabel(ts);
        const freshnessColor = staffFreshnessColor(ts, loc.availability_status);
        return (
          <Marker
            key={loc.user_id}
            position={{ lat: parseFloat(loc.latitude), lng: parseFloat(loc.longitude) }}
            icon={staffIcon(loc.name, loc.user_id, ts, loc.availability_status)}
            zIndex={5}
            title={`${loc.name} — Son görülme: ${freshnessText}`}
            onClick={() => setActiveMarker({ type: 'staff', id: loc.user_id })}
          >
            {activeMarker?.type === 'staff' && activeMarker.id === loc.user_id && (
              <InfoWindow onCloseClick={() => setActiveMarker(null)}>
                <div>
                  <p style={{ fontWeight: 600, color: '#1e293b' }}>{loc.name}</p>
                  {loc.phone && <p style={{ fontSize: 12, color: '#64748b' }}>{loc.phone}</p>}
                  <p style={{ fontSize: 12, color: freshnessColor, marginTop: 4, fontWeight: 600 }}>
                    Son görülme: {freshnessText}
                  </p>
                  <p style={{ fontSize: 11, color: '#94a3b8' }}>
                    {new Date(ts).toLocaleTimeString('tr-TR')}
                  </p>
                </div>
              </InfoWindow>
            )}
          </Marker>
        );
      })}

      {Object.entries(locationTrails).map(([userId, trail]) =>
        trail.length > 1 ? (
          <Polyline
            key={userId}
            path={trail.map(p => ({ lat: p.latitude, lng: p.longitude }))}
            options={{
              strokeColor: '#7c3aed', strokeWeight: 2, strokeOpacity: 0.5,
              icons: [{ icon: { path: 'M 0,-1 0,1', strokeOpacity: 1, scale: 3 }, offset: '0', repeat: '12px' }],
            }}
          />
        ) : null
      )}

      {/* Aktif rotalar (on_the_way personeller) */}
      {Object.entries(renderedDirections).map(([jobId, dir]) => (
        <DirectionsRenderer
          key={`route-${jobId}`}
          directions={dir.result}
          options={{
            suppressMarkers: true,
            polylineOptions: {
              strokeColor: '#2563eb',
              strokeWeight: 4,
              strokeOpacity: 0.7,
            },
          }}
        />
      ))}
    </GoogleMap>
  );
});
